import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Megaphone } from "lucide-react";
import type {
  FirestoreGenerationStatusCode,
  FirestoreProductSubtitleCandidate,
} from "reedy-data/models";

import { BasePanel } from "components/BasePanel";
import type { BasePanelProps } from "components/BasePanel";
import { Button } from "components/button";
import { EmptyList } from "components/empty/EmptyList";
import { ListLoading } from "components/skeletons/ListLoading";
import { SubtitlesList } from "components/SubtitlesList";
import { useAnalytics } from "contexts/AnalyticsContext";
import { generationErrorMessage } from "utils/errors";
import { toast } from "utils/toast";

type PanelLayoutProps = Pick<
  BasePanelProps,
  | "border"
  | "headerClassName"
  | "contentClassName"
  | "className"
  | "wrapper"
  | "testId"
>;

export interface MarketingSubtitlesPanelViewProps {
  productId: string | undefined;
  subtitles: FirestoreProductSubtitleCandidate[] | undefined;
  isGenerating?: boolean;
  isError?: boolean;
  errorStatusCode?: FirestoreGenerationStatusCode | null;
  onGenerate?: () => void | Promise<void>;
  isGenerateDisabled?: boolean;
  hasGenerateError?: boolean;
  panelProps?: PanelLayoutProps;
}

export const MarketingSubtitlesPanelView = ({
  productId,
  subtitles,
  isGenerating,
  isError,
  errorStatusCode,
  onGenerate,
  isGenerateDisabled,
  hasGenerateError,
  panelProps,
}: MarketingSubtitlesPanelViewProps) => {
  const { gaEvent } = useAnalytics();
  const { t } = useTranslation(["components", "general"]);
  const title = t("components:marketingSubtitlesPanel.title");
  const panelLayoutProps: PanelLayoutProps = {
    border: false,
    headerClassName: "rounded-xl",
    ...panelProps,
  };

  useEffect(() => {
    if (hasGenerateError) {
      toast.error(
        t("components:marketingSubtitlesPanel.toasts.generate.error"),
      );
    }
  }, [hasGenerateError, t]);

  const handleGenerate = useCallback(async () => {
    if (!productId || !onGenerate) return;

    toast.success(
      t("components:marketingSubtitlesPanel.toasts.generate.generating"),
    );
    await onGenerate();
  }, [onGenerate, productId, t]);

  const handleCopy = useCallback(
    async (subtitle: FirestoreProductSubtitleCandidate, index: number) => {
      if (!productId) return;

      try {
        await navigator.clipboard.writeText(subtitle.subtitle);
        toast.success(
          t("components:marketingSubtitlesPanel.toasts.copy.success"),
        );

        gaEvent({
          type: "copy_subtitle",
          payload: {
            product_id: productId,
            subtitle: subtitle.subtitle,
            position_in_list: index,
          },
        });
      } catch {
        toast.error(t("components:marketingSubtitlesPanel.toasts.copy.error"));
      }
    },
    [gaEvent, productId, t],
  );

  if (isGenerating) {
    return (
      <BasePanel
        title={title}
        isEmpty
        emptyComponent={
          <EmptyList
            title={t("components:marketingSubtitlesPanel.loading")}
            image={
              <div className="translate-y-16 translate-x-[-7.2rem]">
                <ListLoading />
              </div>
            }
          />
        }
        {...panelLayoutProps}
      />
    );
  }

  if (isError) {
    const errorMessage =
      generationErrorMessage(t, errorStatusCode) ||
      t("components:marketingSubtitlesPanel.error.generation", {
        code: errorStatusCode,
      });

    return (
      <BasePanel
        title={title}
        isEmpty
        emptyComponent={
          <EmptyList
            title={t("components:marketingSubtitlesPanel.error.title")}
            errorMessage={errorMessage}
            variant="error"
            buttons={[
              <Button
                key="retry"
                label={t("components:marketingSubtitlesPanel.actions.retry")}
                onClick={handleGenerate}
                icon="sparkles"
                size="small"
                analyticsId="button_retry_subtitles"
              />,
            ]}
          />
        }
        {...panelLayoutProps}
      />
    );
  }

  const isEmpty = !subtitles?.length;

  return (
    <BasePanel
      title={title}
      isEmpty={isEmpty}
      emptyComponent={
        <EmptyList
          title={t("components:marketingSubtitlesPanel.empty.title")}
          text={t("components:marketingSubtitlesPanel.empty.description")}
          image={
            <div className="size-11 flex justify-center items-center rounded-lg border border-secondary-100">
              <Megaphone className="size-6" />
            </div>
          }
          buttons={[
            <Button
              key="generate"
              variant="primary"
              icon="sparkles"
              size="small"
              label={t("components:marketingSubtitlesPanel.actions.generate")}
              analyticsId="button_generate_subtitles"
              disabled={isGenerateDisabled}
              onClick={handleGenerate}
              className="mt-6"
            />,
          ]}
          classes={{
            title: "text-sm font-semibold text-secondary-800",
            text: "m-0",
          }}
        />
      }
      {...panelLayoutProps}
    >
      <SubtitlesList subtitles={subtitles} onCopy={handleCopy} />
    </BasePanel>
  );
};
